import React from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveLine } from '@nivo/line';
import '../../charts/reproduction/NivoChartCard.css';

// Positive/negative balance is STATE (same good/critical steps as the other
// finance charts). The line itself is neutral ink so color only ever means
// "above or below zero" — carried by each point, and redundantly by its
// position against the dashed zero line.
const GOOD = '#0ca30c';
const CRITICAL = '#d03b3b';
const LINE = '#52514e';

const formatDay = (day) => {
  const d = new Date(day + 'T12:00:00');
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`;
};
const fmtSigned = (symbol, n) => `${n >= 0 ? '+' : '-'}${symbol}${Math.abs(Number(n || 0)).toFixed(2)}`;

// ProjectBalanceChart — the project detail page's balance over time (Trello
// FGxJ9szW): a running total of the project's transactions, one point per
// day with activity. Needs at least two different days to be worth a line.
export const ProjectBalanceChart = ({ transactions, currencySymbol = 'R$' }) => {
  const { t } = useTranslation();

  const byDay = {};
  (transactions || []).forEach(tx => {
    const day = (tx.date || '').substring(0, 10);
    if (!day) return;
    const amount = Number(tx.amount || 0);
    byDay[day] = (byDay[day] || 0) + (tx.type === 'gain' ? amount : -amount);
  });
  const days = Object.keys(byDay).sort();
  if (days.length < 2) return null;

  let running = 0;
  const points = days.map(day => {
    running += byDay[day];
    return { x: day, y: Math.round(running * 100) / 100, change: Math.round(byDay[day] * 100) / 100 };
  });

  // At most ~5 evenly spaced date ticks, same approach as WeightLineChart.js.
  const maxTicks = Math.min(5, days.length);
  const tickValues = Array.from({ length: maxTicks }, (_, i) =>
    days[Math.round((i * (days.length - 1)) / Math.max(1, maxTicks - 1))]
  ).filter((v, i, arr) => arr.indexOf(v) === i);

  return (
    <div className="Chart NivoCard">
      <h3>{t('project_balance_over_time')}</h3>
      <div style={{ height: 260 }}>
        <ResponsiveLine
          data={[{ id: t('project_balance'), data: points }]}
          margin={{ top: 12, right: 20, bottom: 44, left: 72 }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 'auto', max: 'auto', nice: true }}
          axisBottom={{ tickSize: 0, tickPadding: 8, tickValues, format: formatDay, tickRotation: tickValues.length > 4 ? -30 : 0 }}
          axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: 5, format: (v) => `${currencySymbol}${Math.round(v)}` }}
          colors={[LINE]}
          lineWidth={2}
          pointSize={points.length > 20 ? 6 : 9}
          pointColor={({ point }) => (point.data.y >= 0 ? GOOD : CRITICAL)}
          pointBorderWidth={2}
          pointBorderColor="#ffffff"
          markers={[{ axis: 'y', value: 0, lineStyle: { stroke: '#898781', strokeWidth: 1, strokeDasharray: '4 4' } }]}
          enableGridX={false}
          useMesh={true}
          motionConfig="gentle"
          theme={{
            axis: { ticks: { text: { fill: '#898781', fontSize: 11.5 } } },
            grid: { line: { stroke: '#e1e0d9', strokeWidth: 1 } },
          }}
          tooltip={({ point }) => (
            <div className="nivo-tooltip" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
              <strong>{formatDay(point.data.x)}</strong>
              <span>
                <span className="nivo-tooltip-swatch" style={{ background: point.data.y >= 0 ? GOOD : CRITICAL, display: 'inline-block', marginRight: 6 }} />
                {t('project_balance')}: <span className="nivo-tooltip-value">{fmtSigned(currencySymbol, point.data.y)}</span>
              </span>
              <span>{t('project_day_change')}: {fmtSigned(currencySymbol, point.data.change)}</span>
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default ProjectBalanceChart;
